import React from 'react';
import { motion } from 'framer-motion';
import { Target, TrendingUp, PieChart } from 'lucide-react';
import type { DashboardMetrics, CountryCode } from '../utils/calculations';
import { formatCurrency } from '../utils/calculations';
import { Tooltip } from './Tooltip';
interface MainMetricsProps {
  metrics: DashboardMetrics;
  country: CountryCode;
}
export function MainMetrics({
  metrics,
  country
}: MainMetricsProps) {
  const profitPositive = metrics.netProfit >= 0;
  return <motion.div initial={{
    opacity: 0,
    y: 20
  }} animate={{
    opacity: 1,
    y: 0
  }} transition={{
    duration: 0.5,
    delay: 0.2
  }} className="flex flex-col gap-4 h-full" data-id="element-128">
      {/* CPA Meta vs CPA Costeado */}
      <div className="glass-card p-5 group hover:border-cyber-green/30 transition-colors" data-id="element-129">
        <div className="grid grid-cols-2 gap-4" data-id="element-130">
          {/* CPA Meta Ideal */}
          <div data-id="element-131">
            <div className="flex items-center gap-1.5 mb-1" data-id="element-132">
              <Target className="w-3.5 h-3.5 text-cyber-green" data-id="element-133" />
              <h3 className="text-[10px] font-bold tracking-widest text-white/50 uppercase" data-id="element-134">
                CPA Meta
              </h3>
            </div>
            <div className="text-2xl font-black text-cyber-green tracking-tight" data-id="element-135">
              {formatCurrency(metrics.cpaIdeal, country)}
            </div>
            <p className="text-[9px] text-white/30 font-medium mt-0.5" data-id="element-136">
              Le pagas a Meta
            </p>
          </div>
          {/* CPA Costeado */}
          <div data-id="element-137">
            <div className="flex items-center gap-1.5 mb-1" data-id="element-138">
              <Target className="w-3.5 h-3.5 text-salmon" data-id="element-139" />
              <h3 className="text-[10px] font-bold tracking-widest text-white/50 uppercase" data-id="element-140">
                CPA Costeado
              </h3>
              <Tooltip text="Costo real de publicidad por cada entrega exitosa, considerando las devoluciones." data-id="element-141" />
            </div>
            <div className="text-2xl font-black text-salmon tracking-tight" data-id="element-142">
              {formatCurrency(metrics.cpaCosteado, country)}
            </div>
            <p className="text-[9px] text-white/30 font-medium mt-0.5" data-id="element-143">
              Te cuesta por entrega
            </p>
          </div>
        </div>
        <p className="text-[10px] text-white/20 font-medium mt-3 pt-3 border-t border-white/5 text-center" data-id="element-144">
          Lo que le pagas a Meta vs. lo que te cuesta cada entrega real
        </p>
        {/* ROAS */}
        <div className="mt-3 pt-3 border-t border-cyber-green/10" data-id="element-145">
          <div className="flex items-center gap-2 mb-1" data-id="element-146">
            <TrendingUp className="w-3.5 h-3.5 text-cyber-green/60" data-id="element-147" />
            <h4 className="text-xs font-bold tracking-widest text-cyber-green/60 uppercase" data-id="element-148">
              ROAS
            </h4>
            <Tooltip text="Este es el ROAS que debes ver en Meta para alcanzar tu utilidad neta objetivo." data-id="element-149" />
          </div>
          <div className="text-3xl font-black text-cyber-green tracking-tight" data-id="element-150">
            {metrics.roasTarget.toFixed(2)}
          </div>
          <p className="text-[10px] text-cyber-green/40 font-medium mt-1" data-id="element-151">
            El ROAS que debes ver en Meta para cumplir tu utilidad.
          </p>
        </div>
      </div>

      {/* Main Goal: Net Profit */}
      <div className={`glass-card p-6 flex-grow flex flex-col justify-center relative overflow-hidden ${profitPositive ? 'border-salmon/30 shadow-glow-salmon' : 'border-danger-red/30 shadow-glow-red'}`} data-id="element-152">
        <div className={`absolute top-0 right-0 w-32 h-32 ${profitPositive ? 'bg-salmon/10' : 'bg-danger-red/10'} rounded-full blur-3xl -mr-10 -mt-10`} data-id="element-153"></div>
        <div className="relative z-10" data-id="element-154">
          <div className="flex items-center gap-2 mb-2" data-id="element-155">
            <div className={`w-2 h-2 rounded-full ${profitPositive ? 'bg-salmon' : 'bg-danger-red'} animate-pulse`} data-id="element-156"></div>
            <h3 className={`text-sm font-bold tracking-widest uppercase ${profitPositive ? 'text-salmon' : 'text-danger-red'}`} data-id="element-157">
              Utilidad Neta
            </h3>
            <Tooltip text="Ganancia real después de pagar publicidad, producto y logística (Fletes ida y vuelta)." data-id="element-158" />
          </div>
          <div className={`text-5xl font-black tracking-tight mb-1 ${profitPositive ? 'text-white' : 'text-danger-red'}`} data-id="element-159">
            {metrics.netProfit < 0 ? '-' : ''}
            {formatCurrency(metrics.netProfit, country)}
          </div>
          <p className="text-xs text-white/50 font-medium" data-id="element-160">
            {profitPositive ? 'Ganancia por entrega exitosa' : '⚠ Pérdida por entrega — ajusta tus costos'}
          </p>
        </div>
      </div>

      {/* Net Margin */}
      <div className="glass-card p-5 flex items-center justify-between" data-id="element-161">
        <div data-id="element-162">
          <div className="flex items-center gap-2 mb-1" data-id="element-163">
            <PieChart className="w-4 h-4 text-white/70" data-id="element-164" />
            <h3 className="text-xs font-bold tracking-widest text-white/60 uppercase" data-id="element-165">
              Margen Neto
            </h3>
          </div>
          <div className={`text-2xl font-bold tracking-tight ${metrics.netMargin >= 0 ? 'text-white' : 'text-danger-red'}`} data-id="element-166">
            {metrics.netMargin.toFixed(2)}%
          </div>
        </div>
      </div>
    </motion.div>;
}